import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Calendar, CheckCircle, ChevronRight, Clock, Loader2, Plus, Search, Target, Trash2, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const DAILY_TARGET = 480; // 8 hours

const formatMin = (m) => {
  if (!m) return '0m';
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r > 0 ? `${r}m` : ''}` : `${r}m`;
};

const calcMinutes = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
};

// Hide number input spinners
const numInputClass = "w-16 h-7 text-xs text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

const DayPlanner = ({ userName, onClose, isReplan = false }) => {
  const [step, setStep] = useState(isReplan ? 1 : 0); // Skip greeting on re-plan
  const [meetings, setMeetings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Selections
  const [hasMeetingsAnswer, setHasMeetingsAnswer] = useState(null); // null, 'yes', 'no'
  const [selectedMeetings, setSelectedMeetings] = useState([]);
  const [customMeetings, setCustomMeetings] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [otherTasks, setOtherTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const firstName = userName?.split(' ')[0] || 'there';

  const filteredTasks = useMemo(() => {
    if (!taskSearch) return tasks;
    const q = taskSearch.toLowerCase();
    return tasks.filter(t => t.title.toLowerCase().includes(q));
  }, [tasks, taskSearch]);

  // Fetch data when moving past greeting (or immediately on re-plan)
  useEffect(() => {
    if (step === 1) fetchData();
  }, [step]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const today = new Date().toISOString().split('T')[0];

      const fetches = [
        fetch(`${API_URL}/api/meetings?my_meetings=true`, { headers }),
        fetch(`${API_URL}/api/tasks/my-tasks`, { headers }),
        fetch(`${API_URL}/api/tasks/collaborations`, { headers }),
        fetch(`${API_URL}/api/tasks/created-by-me`, { headers }),
        fetch(`${API_URL}/api/timesheets/categories`, { headers }),
      ];

      // If re-planning, also fetch today's existing timesheet
      if (isReplan) {
        fetches.push(fetch(`${API_URL}/api/timesheets/my/${today}`, { headers }));
      }

      const results = await Promise.all(fetches);
      const [meetRes, taskRes, collabRes, createdRes, catRes] = results;
      const tsRes = isReplan ? results[5] : null;

      if (meetRes.ok) {
        const allMeetings = await meetRes.json();
        const todayMeetings = allMeetings.filter(m => {
          if (!m.date_time) return false;
          return m.date_time.startsWith(today) && m.status !== 'Cancelled';
        });
        setMeetings(todayMeetings);
        // Only pre-select scheduled meetings if NOT re-planning (re-plan loads from timesheet)
        if (!isReplan) {
          setSelectedMeetings(todayMeetings.map(m => {
            let startTime = '';
            if (m.date_time && m.date_time.includes('T')) {
              startTime = m.date_time.split('T')[1]?.substring(0, 5) || '';
            }
            return { id: m.id, title: m.title, minutes: 60, startTime, endTime: '' };
          }));
        }
      }

      // Merge tasks from all sources, deduplicate, exclude completed/cancelled
      const seen = new Set();
      const allTasks = [];
      const addTasks = async (res) => {
        if (res.ok) {
          const items = await res.json();
          for (const t of items) {
            if (!seen.has(t.id) && t.status !== 'Completed' && t.status !== 'Cancelled') {
              seen.add(t.id);
              allTasks.push(t);
            }
          }
        }
      };
      await addTasks(taskRes);
      await addTasks(collabRes);
      await addTasks(createdRes);
      setTasks(allTasks);

      if (catRes.ok) {
        const cats = await catRes.json();
        setCategories(cats.filter(c => c.is_active));
      }

      // If re-planning, load existing timesheet entries and populate state
      if (isReplan && tsRes && tsRes.ok) {
        const tsData = await tsRes.json();
        const entries = tsData.entries || [];

        const loadedMeetings = [];
        const loadedTasks = [];
        const loadedOther = [];

        for (const e of entries) {
          if (e.category === 'Meetings') {
            // Parse time from task name like "Title (09:00-10:00)"
            let title = e.task;
            let startTime = '';
            let endTime = '';
            if (title.includes('(') && title.includes(')')) {
              const timeStr = title.substring(title.lastIndexOf('(') + 1, title.lastIndexOf(')'));
              title = title.substring(0, title.lastIndexOf('(')).trim();
              if (timeStr.includes('-')) {
                [startTime, endTime] = timeStr.split('-').map(s => s.trim());
              } else {
                startTime = timeStr.trim();
              }
            }
            loadedMeetings.push({ id: `cm-${Date.now()}-${loadedMeetings.length}`, title, startTime, endTime, minutes: e.minutes || 0 });
          } else if (e.task_id) {
            // Matched to an existing task
            loadedTasks.push({ id: e.task_id, title: e.task, minutes: e.minutes || 60, priority: '' });
          } else {
            // Other/custom entry
            loadedOther.push({ task: e.task, category: e.category || 'Core Work', minutes: e.minutes || 30 });
          }
        }

        if (loadedMeetings.length > 0) {
          setHasMeetingsAnswer('yes');
          setCustomMeetings(loadedMeetings);
        } else {
          setHasMeetingsAnswer('no');
        }
        if (loadedTasks.length > 0) setSelectedTasks(loadedTasks);
        if (loadedOther.length > 0) setOtherTasks(loadedOther);
      } else if (!isReplan) {
        // Fresh plan: pre-select tasks due today
        const dueTodayTasks = allTasks.filter(t => t.due_date === today);
        setSelectedTasks(dueTodayTasks.map(t => ({ id: t.id, title: t.title, minutes: 60, priority: t.priority })));
      }

    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const totalPlanned = useMemo(() => {
    const meetMin = selectedMeetings.reduce((s, m) => s + (parseInt(m.minutes) || 0), 0);
    const customMeetMin = customMeetings.reduce((s, m) => s + (parseInt(m.minutes) || 0), 0);
    const taskMin = selectedTasks.reduce((s, t) => s + (parseInt(t.minutes) || 0), 0);
    const otherMin = otherTasks.reduce((s, t) => s + (parseInt(t.minutes) || 0), 0);
    return meetMin + customMeetMin + taskMin + otherMin;
  }, [selectedMeetings, customMeetings, selectedTasks, otherTasks]);

  const remaining = DAILY_TARGET - totalPlanned;

  const toggleMeeting = (meeting) => {
    setSelectedMeetings(prev => {
      const exists = prev.find(m => m.id === meeting.id);
      if (exists) return prev.filter(m => m.id !== meeting.id);
      let startTime = '';
      if (meeting.date_time && meeting.date_time.includes('T')) {
        startTime = meeting.date_time.split('T')[1]?.substring(0, 5) || '';
      }
      return [...prev, { id: meeting.id, title: meeting.title, startTime, endTime: '', minutes: 60 }];
    });
  };

  const updateMeetingMin = (id, min) => {
    setSelectedMeetings(prev => prev.map(m => m.id === id ? { ...m, minutes: parseInt(min) || 0 } : m));
  };

  const toggleTask = (task) => {
    setSelectedTasks(prev => {
      const exists = prev.find(t => t.id === task.id);
      if (exists) return prev.filter(t => t.id !== task.id);
      return [...prev, { id: task.id, title: task.title, minutes: 60, priority: task.priority }];
    });
  };

  const updateTaskMin = (id, min) => {
    setSelectedTasks(prev => prev.map(t => t.id === id ? { ...t, minutes: parseInt(min) || 0 } : t));
  };

  const addOtherTask = () => {
    setOtherTasks([...otherTasks, { id: `other-${Date.now()}`, task: '', category: '', minutes: 60 }]);
  };

  const updateOther = (idx, field, val) => {
    const updated = [...otherTasks];
    updated[idx] = { ...updated[idx], [field]: field === 'minutes' ? parseInt(val) || 0 : val };
    setOtherTasks(updated);
  };

  const removeOther = (idx) => {
    setOtherTasks(otherTasks.filter((_, i) => i !== idx));
  };

  const handleSaveToTimesheet = async () => {
    setSaving(true);
    try {
      // Derive minutes from startTime + endTime when the user set them but left minutes at 0
      const deriveMinutes = (m) => {
        if (m.minutes > 0) return m.minutes;
        if (m.startTime && m.endTime) {
          const [sh, sm] = m.startTime.split(':').map(Number);
          const [eh, em] = m.endTime.split(':').map(Number);
          const diff = (eh * 60 + em) - (sh * 60 + sm);
          if (diff > 0) return diff;
        }
        return 0;
      };
      const entries = [];
      selectedMeetings.forEach(m => {
        const mins = deriveMinutes(m);
        if (mins > 0) {
          const timeStr = m.startTime && m.endTime ? `${m.startTime}-${m.endTime}` : m.startTime || '';
          entries.push({ task: timeStr ? `${m.title} (${timeStr})` : m.title, category: 'Meetings', minutes: mins });
        }
      });
      customMeetings.forEach(m => {
        if (!m.title) return;
        const mins = deriveMinutes(m);
        if (mins > 0) {
          const timeStr = m.startTime && m.endTime ? `${m.startTime}-${m.endTime}` : m.startTime || '';
          entries.push({ task: timeStr ? `${m.title} (${timeStr})` : m.title, category: 'Meetings', minutes: mins });
        }
      });
      selectedTasks.forEach(t => {
        if (t.minutes > 0) entries.push({ task: t.title, task_id: t.id, category: 'Core Work', minutes: t.minutes });
      });
      otherTasks.forEach(t => {
        if (t.task && t.minutes > 0) entries.push({ task: t.task, category: t.category || 'Core Work', minutes: t.minutes });
      });

      if (entries.length === 0) {
        toast.error('Add at least one meeting or task with a duration to save your plan');
        setSaving(false);
        return;
      }

      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const today = new Date().toISOString().split('T')[0];

      // 1. Auto-create tasks for custom meetings and other tasks (as drafts)
      const taskCreationPromises = [];

      // Create tasks for custom meetings
      customMeetings.forEach(m => {
        if (m.title && m.minutes > 0) {
          const timeStr = m.startTime && m.endTime ? `${m.startTime}-${m.endTime}` : m.startTime || '';
          taskCreationPromises.push(
            fetch(`${API_URL}/api/tasks/`, {
              method: 'POST', headers,
              body: JSON.stringify({
                title: timeStr ? `${m.title} (${timeStr})` : m.title,
                description: `Meeting planned via Day Planner`,
                task_type: 'self',
                status: 'In Progress',
                priority: 'Medium',
                due_date: today,
                category: 'Meetings',
                draft: true,
                source: 'day_planner',
              })
            }).catch(err => console.error('Failed to create meeting task:', err))
          );
        }
      });

      // Create tasks for other work items
      otherTasks.forEach(t => {
        if (t.task && t.minutes > 0) {
          taskCreationPromises.push(
            fetch(`${API_URL}/api/tasks/`, {
              method: 'POST', headers,
              body: JSON.stringify({
                title: t.task,
                description: `Task planned via Day Planner`,
                task_type: 'self',
                status: 'In Progress',
                priority: 'Medium',
                due_date: today,
                category: t.category || 'Core Work',
                draft: true,
                source: 'day_planner',
              })
            }).catch(err => console.error('Failed to create other task:', err))
          );
        }
      });

      // Fire task creations in parallel (non-blocking for timesheet save)
      if (taskCreationPromises.length > 0) {
        Promise.all(taskCreationPromises).then(results => {
          const created = results.filter(r => r && r.ok);
          if (created.length > 0) {
            console.log(`Auto-created ${created.length} tasks from Day Planner`);
          }
        });
      }

      // 2. Save timesheet entries as Draft (visible only to employee until submitted)
      const res = await fetch(`${API_URL}/api/timesheets`, {
        method: 'POST', headers,
        body: JSON.stringify({ date: today, entries, source: 'day_planner' })
      });

      if (res.ok) {
        const taskCount = taskCreationPromises.length;
        const msg = taskCount > 0
          ? `Day planned! ${entries.length} entries & ${taskCount} tasks drafted (${formatMin(totalPlanned)} of 8h). Submit from your Timesheet to share with your manager.`
          : `Day planned! ${entries.length} entries drafted (${formatMin(totalPlanned)} of 8h). Submit from your Timesheet to share with your manager.`;
        toast.success(msg);
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save timesheet');
      }
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // Progress bar
  const pct = Math.min((totalPlanned / DAILY_TARGET) * 100, 100);

  // ─── STEP RENDERERS ───

  const renderGreeting = () => (
    <div className="text-center py-4">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
        <Calendar className="h-7 w-7 text-blue-600" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">Hi {firstName}!</h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        It's another work day. Let's start by planning your day together.
      </p>
      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={onClose} data-testid="planner-skip">
          Continue Without Planning
        </Button>
        <Button onClick={() => setStep(1)} data-testid="planner-start">
          Plan My Day <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderMeetings = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Do you have any meetings today?</h3>
      </div>

      {/* Yes / No selection */}
      {hasMeetingsAnswer === null ? (
        <div className="flex gap-3 justify-center py-4">
          <Button variant="outline" className="px-8 py-3 text-base" onClick={() => { setHasMeetingsAnswer('no'); setSelectedMeetings([]); }}
            data-testid="meetings-no">
            No
          </Button>
          <Button className="px-8 py-3 text-base" onClick={() => setHasMeetingsAnswer('yes')}
            data-testid="meetings-yes">
            Yes
          </Button>
        </div>
      ) : hasMeetingsAnswer === 'no' ? (
        <div className="text-center py-4 text-gray-500">
          <p className="text-sm">No meetings — great, more focus time!</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500">Select the meetings you'll attend — set start & end times.</p>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-4 text-gray-400">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No scheduled meetings found for today. You can add them manually below.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {meetings.map(m => {
                const selected = selectedMeetings.find(sm => sm.id === m.id);
                return (
                  <div key={m.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => toggleMeeting(m)}
                    data-testid={`meeting-${m.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                          {selected && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                        <span className="text-sm font-medium text-gray-800">{m.title}</span>
                      </div>
                      {selected && (
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <Input type="time" value={selected.startTime || ''}
                            onChange={e => setSelectedMeetings(prev => prev.map(sm => sm.id === m.id ? { ...sm, startTime: e.target.value, minutes: calcMinutes(e.target.value, sm.endTime) } : sm))}
                            className="w-24 h-7 text-xs" placeholder="Start" />
                          <span className="text-xs text-gray-400">to</span>
                          <Input type="time" value={selected.endTime || ''}
                            onChange={e => setSelectedMeetings(prev => prev.map(sm => sm.id === m.id ? { ...sm, endTime: e.target.value, minutes: calcMinutes(sm.startTime, e.target.value) } : sm))}
                            className="w-24 h-7 text-xs" placeholder="End" />
                          <span className="text-xs text-blue-600 font-medium w-12 text-right">{formatMin(selected.minutes)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add custom meetings */}
          <div className="space-y-2">
            {customMeetings.map((cm, idx) => (
              <div key={cm.id} className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-gray-300 bg-gray-50" data-testid={`custom-meeting-${idx}`}>
                <Input value={cm.title} onChange={e => {
                  const updated = [...customMeetings];
                  updated[idx] = { ...updated[idx], title: e.target.value };
                  setCustomMeetings(updated);
                }} placeholder="Meeting name" className="text-sm flex-1" />
                <Input type="time" value={cm.startTime || ''} onChange={e => {
                  const updated = [...customMeetings];
                  updated[idx] = { ...updated[idx], startTime: e.target.value, minutes: calcMinutes(e.target.value, cm.endTime) };
                  setCustomMeetings(updated);
                }} className="w-24 h-9 text-xs" placeholder="Start" />
                <span className="text-xs text-gray-400">to</span>
                <Input type="time" value={cm.endTime || ''} onChange={e => {
                  const updated = [...customMeetings];
                  updated[idx] = { ...updated[idx], endTime: e.target.value, minutes: calcMinutes(cm.startTime, e.target.value) };
                  setCustomMeetings(updated);
                }} className="w-24 h-9 text-xs" placeholder="End" />
                <span className="text-xs text-blue-600 font-medium w-12 text-right">{formatMin(cm.minutes)}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                  onClick={() => setCustomMeetings(customMeetings.filter((_, i) => i !== idx))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setCustomMeetings([...customMeetings, { id: `cm-${Date.now()}`, title: '', startTime: '', endTime: '', minutes: 60 }])}
              data-testid="add-custom-meeting">
              <Plus className="h-4 w-4 mr-1" /> Add Meeting
            </Button>
          </div>

          <button className="text-xs text-blue-600 hover:underline" onClick={() => { setHasMeetingsAnswer(null); setSelectedMeetings([]); setCustomMeetings([]); }}>Change answer</button>
          {selectedMeetings.length === 0 && customMeetings.length === 0 && (
            <p className="text-xs text-orange-500 mt-1">Please select or add at least one meeting to proceed.</p>
          )}
        </>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={() => { setStep(0); setHasMeetingsAnswer(null); }}>Back</Button>
        <Button size="sm" onClick={() => setStep(2)} disabled={
          hasMeetingsAnswer === null ||
          (hasMeetingsAnswer === 'yes' && selectedMeetings.length === 0 && customMeetings.length === 0) ||
          (hasMeetingsAnswer === 'yes' && selectedMeetings.every(m => !m.minutes) && customMeetings.every(m => !m.title || !m.minutes))
        } data-testid="planner-next-tasks">
          Next: Tasks <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderTasks = () => {
    const today = new Date().toISOString().split('T')[0];
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Your tasks</h3>
          <p className="text-sm text-gray-500">Select what you'll work on today and estimate the time.</p>
        </div>

        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search tasks..." value={taskSearch} onChange={e => setTaskSearch(e.target.value)}
            className="pl-9 text-sm" data-testid="task-search" />
        </div>

        {tasks.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No active tasks found</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {filteredTasks.map(t => {
              const selected = selectedTasks.find(st => st.id === t.id);
              const isDueToday = t.due_date === today;
              const isOverdue = t.due_date && t.due_date < today;
              return (
                <div key={t.id}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                  onClick={() => toggleTask(t)}
                  data-testid={`task-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                        {selected && <CheckCircle className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-sm font-medium text-gray-800 truncate">{t.title}</span>
                      {isDueToday && <Badge className="text-[9px] bg-amber-100 text-amber-700 shrink-0">Due today</Badge>}
                      {isOverdue && <Badge className="text-[9px] bg-red-100 text-red-700 shrink-0">Overdue</Badge>}
                      {t.priority === 'High' && <Badge className="text-[9px] bg-red-50 text-red-600 shrink-0">High</Badge>}
                    </div>
                    {selected && (
                      <div className="flex items-center gap-1 shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                        <Input type="number" min="15" max="480" value={selected.minutes}
                          onChange={e => updateTaskMin(t.id, e.target.value)}
                          className={numInputClass} />
                        <span className="text-xs text-gray-400">min</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredTasks.length === 0 && taskSearch && (
              <p className="text-center text-sm text-gray-400 py-4">No tasks matching "{taskSearch}"</p>
            )}
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
          <Button size="sm" onClick={() => setStep(3)} data-testid="planner-next-other">
            Next: Other Work <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  const renderOther = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Any other tasks today?</h3>
        <p className="text-sm text-gray-500">Add anything else you plan to work on — admin, calls, planning, etc.</p>
      </div>

      {/* Hours summary */}
      <div className="flex items-center gap-2 text-sm">
        <Clock className="h-4 w-4 text-gray-400" />
        <span className="text-gray-600">Planned so far: <span className="font-semibold">{formatMin(totalPlanned)}</span></span>
        {remaining > 0 && <span className="text-orange-500">({formatMin(remaining)} remaining of 8h)</span>}
        {remaining <= 0 && <span className="text-green-600">(Target met)</span>}
      </div>

      <div className="space-y-2 max-h-40 overflow-y-auto">
        {otherTasks.map((t, idx) => (
          <div key={t.id} className="flex items-center gap-2" data-testid={`other-task-${idx}`}>
            <Input value={t.task} onChange={e => updateOther(idx, 'task', e.target.value)}
              placeholder="Task description" className="text-sm flex-1" />
            <select value={t.category} onChange={e => updateOther(idx, 'category', e.target.value)}
              className="h-9 rounded-md border border-gray-200 text-sm px-2 w-36">
              <option value="">Category</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <Input type="number" min="15" max="480" value={t.minutes}
              onChange={e => updateOther(idx, 'minutes', e.target.value)}
              className={`${numInputClass} w-16`} />
            <span className="text-xs text-gray-400 shrink-0">min</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400"
              onClick={() => removeOther(idx)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={addOtherTask} data-testid="add-other-task">
        <Plus className="h-4 w-4 mr-1" /> Add Task
      </Button>

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={() => setStep(2)}>Back</Button>
        <Button size="sm" onClick={() => setStep(4)} data-testid="planner-review">
          Review Plan <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderSummary = () => {
    const allEntries = [
      ...selectedMeetings.filter(m => m.minutes > 0).map(m => ({ task: m.time ? `${m.title} (${m.time})` : m.title, category: 'Meetings', minutes: m.minutes })),
      ...customMeetings.filter(m => m.title && m.minutes > 0).map(m => ({ task: m.time ? `${m.title} (${m.time})` : m.title, category: 'Meetings', minutes: m.minutes })),
      ...selectedTasks.filter(t => t.minutes > 0).map(t => ({ task: t.title, category: 'Core Work', minutes: t.minutes })),
      ...otherTasks.filter(t => t.task && t.minutes > 0).map(t => ({ task: t.task, category: t.category || 'Core Work', minutes: t.minutes })),
    ];

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Your day plan</h3>
          <p className="text-sm text-gray-500">Review and save to your timesheet. You can always edit later.</p>
        </div>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">{formatMin(totalPlanned)} planned</span>
            <span className="text-gray-400">Target: 8h</span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : 'bg-orange-400'}`}
              style={{ width: `${pct}%` }} />
          </div>
        </div>

        {allEntries.length === 0 ? (
          <p className="text-center text-gray-400 py-4 text-sm">No entries planned yet</p>
        ) : (
          <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-600">Task</th>
                  <th className="text-left p-2 font-medium text-gray-600">Category</th>
                  <th className="text-right p-2 font-medium text-gray-600">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {allEntries.map((e, i) => (
                  <tr key={i}>
                    <td className="p-2 text-gray-800">{e.task}</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">{e.category}</Badge></td>
                    <td className="p-2 text-right font-medium">{formatMin(e.minutes)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={2} className="p-2 text-right">Total</td>
                  <td className="p-2 text-right">{formatMin(totalPlanned)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center italic">
          This plan will be saved to your timesheet. A well-planned day keeps you and your team aligned.
        </p>

        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={() => setStep(3)}>Back</Button>
          <Button onClick={handleSaveToTimesheet} disabled={saving || allEntries.length === 0} data-testid="planner-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Save to Timesheet
          </Button>
        </div>
      </div>
    );
  };

  // Step indicators
  const steps = ['Welcome', 'Meetings', 'Tasks', 'Other', 'Summary'];

  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm flex items-center justify-center" data-testid="day-planner">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Step progress */}
        {step > 0 && (
          <div className="px-6 pt-4 pb-2 flex items-center gap-1">
            {steps.slice(1).map((s, i) => (
              <React.Fragment key={s}>
                <div className={`h-1.5 flex-1 rounded-full ${i + 1 <= step ? 'bg-blue-500' : 'bg-gray-200'}`} />
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="px-6 pb-6 pt-2">
          {/* Close button */}
          <div className="flex justify-end mb-1">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="planner-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {step === 0 && renderGreeting()}
          {step === 1 && renderMeetings()}
          {step === 2 && renderTasks()}
          {step === 3 && renderOther()}
          {step === 4 && renderSummary()}
        </div>
      </div>
    </div>
  );
};

export default DayPlanner;
